"use client";

/**
 * TanStack Query hooks for the reports CRUD surface. All mutations
 * invalidate the relevant query keys + perform optimistic updates so the
 * canvas stays responsive during edits.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";

import { reportsApi, type ReportDetail, type ReportSummary, type UpdateReportInput } from "../api/client";

const KEYS = {
  all: ["reports"] as const,
  list: (params?: { include_templates?: boolean; templates_only?: boolean; mode?: string }) =>
    [...KEYS.all, "list", params ?? {}] as const,
  detail: (id: string) => [...KEYS.all, "detail", id] as const,
  versions: (id: string) => [...KEYS.all, "versions", id] as const,
};

export function useReports(
  params?: { include_templates?: boolean; templates_only?: boolean; mode?: string },
  options?: Omit<UseQueryOptions<ReportSummary[]>, "queryKey" | "queryFn">,
) {
  return useQuery({
    queryKey: KEYS.list(params),
    queryFn: () => reportsApi.list(params),
    staleTime: 30_000,
    ...options,
  });
}

export function useReport(id: string | undefined) {
  return useQuery({
    queryKey: id ? KEYS.detail(id) : ["reports", "detail", "none"],
    queryFn: () => reportsApi.get(id!),
    enabled: Boolean(id),
    staleTime: 5_000,
  });
}

export function useCreateReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: reportsApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
    },
  });
}

export function useUpdateReport(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateReportInput) => reportsApi.update(id, input),
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: KEYS.detail(id) });
      const prev = qc.getQueryData<ReportDetail>(KEYS.detail(id));
      if (prev) {
        qc.setQueryData<ReportDetail>(KEYS.detail(id), {
          ...prev,
          ...input,
          spec: input.spec ?? prev.spec,
        } as ReportDetail);
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(KEYS.detail(id), ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: KEYS.detail(id) });
      qc.invalidateQueries({ queryKey: KEYS.versions(id) });
      qc.invalidateQueries({ queryKey: [...KEYS.all, "list"] });
    },
  });
}

export function useDeleteReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => reportsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
    },
  });
}

export function useDuplicateReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => reportsApi.duplicate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
    },
  });
}

export function useReportVersions(id: string | undefined) {
  return useQuery({
    queryKey: id ? KEYS.versions(id) : ["reports", "versions", "none"],
    queryFn: () => reportsApi.versions(id!),
    enabled: Boolean(id),
    staleTime: 10_000,
  });
}

export function useRestoreReportVersion(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (version: number) => reportsApi.restore(id, version),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.detail(id) });
      qc.invalidateQueries({ queryKey: KEYS.versions(id) });
    },
  });
}

export function useWizardHealth(since?: string) {
  return useQuery({
    queryKey: ["reports", "wizard-health", since ?? "default"],
    queryFn: () => reportsApi.wizardHealth(since),
    refetchInterval: 30_000,
  });
}

export const reportQueryKeys = KEYS;
